// User list with search + role filter. Click a row → /admin/users/:id.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listUsers } from '../../services/adminService';
import DataTable from '../common/DataTable';

const roleBadge = (r) => <span className={`badge badge-${r}`}>{r}</span>;

export default function AdminUsers() {
  const [data, setData] = useState({ users: [], pagination: { page: 1, total: 0, page_size: 50 } });
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  // Status filter is client-side — the backend doesn't expose ?is_active=
  // and applying it to the current page is good enough at admin scale.
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listUsers({ q, role, page })
      .then(d => { if (alive) setData(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [q, role, page]);

  const visible = data.users.filter(u => {
    if (status === 'active') return u.is_active;
    if (status === 'inactive') return !u.is_active;
    return true;
  });

  const resetFilters = () => { setQ(''); setRole(''); setStatus(''); setPage(1); };
  const hasFilters = q || role || status;

  return (
    <>
      <div className="portal-content-header">
        <h1>Users</h1>
      </div>

      <div className="portal-filter-bar">
        <input
          type="search"
          className="portal-search"
          placeholder="Search by name, email, username"
          value={q}
          onChange={e => { setQ(e.target.value); setPage(1); }}
        />
        <select
          className="portal-search"
          value={role}
          onChange={e => { setRole(e.target.value); setPage(1); }}
        >
          <option value="">All roles</option>
          <option value="customer">Customer</option>
          <option value="merchant">Merchant</option>
          <option value="admin">Admin</option>
        </select>
        <select className="portal-search" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All status</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        {hasFilters && (
          <button type="button" className="portal-button secondary" onClick={resetFilters}>Clear</button>
        )}
        <span className="meta">
          {status ? `${visible.length} of ${data.pagination.total}` : `${data.pagination.total}`} users
        </span>
      </div>

      {loading && <div className="portal-empty">Loading…</div>}

      <DataTable
        columns={[
          {
            key: 'username', label: 'User',
            render: u => <Link to={`/admin/users/${u.id}`}>{u.username}</Link>,
          },
          { key: 'email', label: 'Email' },
          { key: 'role', label: 'Role', render: u => roleBadge(u.role) },
          {
            key: 'is_active', label: 'Status',
            render: u => <span className={`badge badge-${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Inactive'}</span>,
          },
          { key: 'task_count', label: 'Tasks' },
          { key: 'last_login', label: 'Last login', render: u => u.last_login ? new Date(u.last_login).toLocaleDateString() : '—' },
          { key: 'date_joined', label: 'Joined', render: u => new Date(u.date_joined).toLocaleDateString() },
        ]}
        rows={visible}
        emptyMessage={hasFilters ? 'No users match the current filters.' : 'No users yet.'}
      />

      {data.pagination.total > data.pagination.page_size && (
        <div className="portal-pagination">
          <button type="button" className="portal-button secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span>Page {data.pagination.page}</span>
          <button
            type="button"
            className="portal-button secondary"
            disabled={page * data.pagination.page_size >= data.pagination.total}
            onClick={() => setPage(p => p + 1)}
          >Next</button>
        </div>
      )}
    </>
  );
}
